import React, { useMemo } from "react";
import {
  Box,
  HStack,
  Text,
  Button,
  Spinner,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
} from "@chakra-ui/react";
import { MdHistory } from "react-icons/md";
import { formatDate } from "../helpers/Functions";

const TabelaHistoricoAlteracoes = ({
  logsData,
  papeletaData,
  mostrarTodosLogs,
  setMostrarTodosLogs,
  loadingLogs,
  iconesEvento,
}) => {
  // Função para agrupar logs por id_marcacao
  const agruparLogsPorMarcacao = (logs) => {
    const grupos = new Map();

    logs.forEach((log) => {
      // Chave única: id_marcacao + data_original + nome_evento
      // Agrupa TODOS os logs do mesmo evento (inserção, alteração, exclusão)
      const chave = `${log.id_marcacao || "sem_marcacao"}_${
        log.data_original
      }_${log.nome_evento}`;

      // Debug específico para o evento problemático
      if (log.id_marcacao === 17038 && log.data_original === "2025-08-01") {
        console.log("🔍 DEBUG AGRUPAMENTO:", {
          log: {
            campo: log.campo_alteracao,
            data_alteracao: log.data_alteracao,
            hora_alteracao: log.hora_alteracao,
            hora_original: log.hora_original,
            hora_novo: log.hora_novo,
          },
          chave: chave,
          ja_existe: grupos.has(chave),
        });
      }

      if (!grupos.has(chave)) {
        grupos.set(chave, {
          id_marcacao: log.id_marcacao,
          data_original: log.data_original,
          nome_evento: log.nome_evento,
          nome_motorista: log.nome_motorista,
          logs: [],
          tipo: "Alterado",
          originalInicial: "",
          novoInicial: "",
          originalFinal: "",
          novoFinal: "",
        });
      }
      grupos.get(chave).logs.push(log);
    });

    // Processar cada grupo
    grupos.forEach((grupo) => {
      // Ordenar logs por data/hora de alteração (mais recente primeiro)
      const logsOrdenados = grupo.logs.sort((a, b) => {
        const dataA = new Date(`${a.data_alteracao}T${a.hora_alteracao}`);
        const dataB = new Date(`${b.data_alteracao}T${b.hora_alteracao}`);
        return dataB - dataA; // Mais recente primeiro
      });

      // Função para verificar se um log corresponde ao estado final na papeleta
      const logCorrespondeAoEstadoFinal = (log) => {
        if (!papeletaData || !log.id_marcacao) return false;

        // Encontrar o evento correspondente na papeleta
        const eventoPapeleta = papeletaData.find(
          (evento) => evento.id === log.id_marcacao
        );
        if (!eventoPapeleta) return false;

        // Verificar se os horários do log correspondem aos horários finais da papeleta
        const campo = log.campo_alteracao;

        if (campo === "horario_inicial" || campo === "inserido_inicial") {
          return log.hora_novo === eventoPapeleta.hora_inicial;
        } else if (campo === "horario_final" || campo === "inserido_final") {
          return log.hora_novo === eventoPapeleta.hora_final;
        }

        return false;
      };

      // Para "Ver apenas alterações finais", filtrar logs que correspondem ao estado final
      if (!mostrarTodosLogs) {
        // Manter apenas logs que correspondem ao estado final da papeleta
        grupo.logs = grupo.logs.filter((log) =>
          logCorrespondeAoEstadoFinal(log)
        );

        // Se não há logs que correspondem ao estado final, pular este grupo
        if (grupo.logs.length === 0) {
          return;
        }

        // Reordenar após filtragem
        grupo.logs.sort((a, b) => {
          const dataA = new Date(`${a.data_alteracao}T${a.hora_alteracao}`);
          const dataB = new Date(`${b.data_alteracao}T${b.hora_alteracao}`);
          return dataB - dataA;
        });
      }

      // Verificar se o último log é uma exclusão
      const ultimoLog = logsOrdenados[0];
      const isUltimaOperacaoExclusao = ultimoLog.campo_alteracao === "excluido";

      // Debug específico para o evento problemático
      if (grupo.id_marcacao === 17038 && grupo.data_original === "2025-08-01") {
        console.log("🔍 DEBUG EVENTO PROBLEMÁTICO:", {
          id_marcacao: grupo.id_marcacao,
          data_original: grupo.data_original,
          nome_evento: grupo.nome_evento,
          logs_ordenados: logsOrdenados.map((log) => ({
            campo: log.campo_alteracao,
            data: log.data_alteracao,
            hora: log.hora_alteracao,
            hora_original: log.hora_original,
            hora_novo: log.hora_novo,
          })),
          ultimo_log: ultimoLog.campo_alteracao,
          is_ultima_operacao_exclusao: isUltimaOperacaoExclusao,
        });
      }

      // Determinar tipo baseado nos campos de alteração
      const temHorarioInicial = logsOrdenados.some(
        (log) => log.campo_alteracao === "horario_inicial"
      );
      const temHorarioFinal = logsOrdenados.some(
        (log) => log.campo_alteracao === "horario_final"
      );
      const temInseridoInicial = logsOrdenados.some(
        (log) => log.campo_alteracao === "inserido_inicial"
      );
      const temInseridoFinal = logsOrdenados.some(
        (log) => log.campo_alteracao === "inserido_final"
      );
      const temExcluido = logsOrdenados.some(
        (log) => log.campo_alteracao === "excluido"
      );

      // Lógica de determinação do tipo
      if (temExcluido) {
        grupo.tipo = "Excluído";
      } else if (temInseridoInicial || temInseridoFinal) {
        grupo.tipo = "Inserido";
      } else if (temHorarioInicial || temHorarioFinal) {
        grupo.tipo = "Alterado";
      } else {
        grupo.tipo = "Alterado";
      }

      // Marcar se deve ser filtrado (última operação foi exclusão)
      grupo.isUltimaOperacaoExclusao = isUltimaOperacaoExclusao;

      // Consolidar horários
      if (isUltimaOperacaoExclusao) {
        // Se a última operação é exclusão, mostrar apenas os horários da exclusão
        const logExclusao = logsOrdenados.find(
          (log) => log.campo_alteracao === "excluido"
        );
        if (logExclusao) {
          grupo.originalInicial = logExclusao.hora_original || "—";
          grupo.novoInicial = "—";
          grupo.originalFinal = logExclusao.hora_original || "—";
          grupo.novoFinal = "—";
        }

        // Debug específico para o evento problemático
        if (
          grupo.id_marcacao === 17038 &&
          grupo.data_original === "2025-08-01"
        ) {
          console.log("🔍 DEBUG CONSOLIDAÇÃO EXCLUSÃO:", {
            log_exclusao: logExclusao,
            original_inicial: grupo.originalInicial,
            novo_inicial: grupo.novoInicial,
            original_final: grupo.originalFinal,
            novo_final: grupo.novoFinal,
          });
        }
      } else {
        // Se não é exclusão, processar todos os logs normalmente
        // Buscar TODOS os logs do mesmo id_marcacao para pegar o valor original do evento
        // IMPORTANTE: Usar logsData (todos os logs) em vez de logs (logs filtrados)
        const todosLogsDoEvento = logsData.filter(
          (log) => log.id_marcacao === grupo.id_marcacao
        );

        // Para cada campo, detectar se houve inserção e separar somente alterações reais
        const hasInseridoInicial = todosLogsDoEvento.some(
          (log) => log.campo_alteracao === "inserido_inicial"
        );
        const hasInseridoFinal = todosLogsDoEvento.some(
          (log) => log.campo_alteracao === "inserido_final"
        );

        // Só considerar logs de alteração reais para buscar o "original"
        const logsHorarioInicial = todosLogsDoEvento.filter(
          (log) => log.campo_alteracao === "horario_inicial"
        );
        const logsHorarioFinal = todosLogsDoEvento.filter(
          (log) => log.campo_alteracao === "horario_final"
        );

        // Definir "original inicial": se houve inserção, sempre "—"; caso contrário, usar o mais antigo
        if (hasInseridoInicial) {
          grupo.originalInicial = "—";
        } else if (logsHorarioInicial.length > 0) {
          const logOriginalInicial = logsHorarioInicial.reduce(
            (menor, atual) => {
              const horaMenor = menor.hora_alteracao;
              const horaAtual = atual.hora_alteracao;
              return horaAtual < horaMenor ? atual : menor;
            }
          );
          grupo.originalInicial = logOriginalInicial.hora_original || "—";
        }

        // Definir "original final": se houve inserção, sempre "—"; caso contrário, usar o mais antigo
        if (hasInseridoFinal) {
          grupo.originalFinal = "—";
        } else if (logsHorarioFinal.length > 0) {
          const logOriginalFinal = logsHorarioFinal.reduce((menor, atual) => {
            const horaMenor = menor.hora_alteracao;
            const horaAtual = atual.hora_alteracao;
            return horaAtual < horaMenor ? atual : menor;
          });
          grupo.originalFinal = logOriginalFinal.hora_original || "—";
        }

        // Depois, processar os logs do grupo atual para obter os valores finais
        // Primeiro, tentar processar normalmente com os logs filtrados
        logsOrdenados.forEach((log) => {
          const campo = log.campo_alteracao;

          if (campo === "horario_inicial") {
            // Para horário novo, sempre usar o MAIS RECENTE
            grupo.novoInicial = log.hora_novo || "—";
          } else if (campo === "inserido_inicial") {
            grupo.novoInicial = log.hora_novo || "—";
          } else if (campo === "horario_final") {
            // Para horário novo, sempre usar o MAIS RECENTE
            grupo.novoFinal = log.hora_novo || "—";
          } else if (campo === "inserido_final") {
            grupo.novoFinal = log.hora_novo || "—";
          }
        });

        // Se há inserção e algum dos horários não foi preenchido (foi removido pelo filtro),
        // buscar diretamente dos logs de inserção para garantir que ambos sejam exibidos
        if (
          (hasInseridoInicial || hasInseridoFinal) &&
          (!grupo.novoInicial || !grupo.novoFinal)
        ) {
          // Buscar logs de inserção diretamente de todosLogsDoEvento (todos os logs, não apenas filtrados)
          const logInseridoInicial = todosLogsDoEvento.find(
            (log) => log.campo_alteracao === "inserido_inicial"
          );
          const logInseridoFinal = todosLogsDoEvento.find(
            (log) => log.campo_alteracao === "inserido_final"
          );

          // Preencher horário inicial se não foi preenchido e encontrou log de inserção
          if (!grupo.novoInicial && logInseridoInicial) {
            grupo.novoInicial = logInseridoInicial.hora_novo || "—";
          }

          // Preencher horário final se não foi preenchido e encontrou log de inserção
          if (!grupo.novoFinal && logInseridoFinal) {
            grupo.novoFinal = logInseridoFinal.hora_novo || "—";
          }

          // Se ainda não encontrou, buscar do evento na papeleta como fallback
          if ((!grupo.novoInicial || !grupo.novoFinal) && papeletaData) {
            const eventoPapeleta = papeletaData.find(
              (evento) => evento.id === grupo.id_marcacao
            );
            if (eventoPapeleta) {
              if (!grupo.novoInicial && eventoPapeleta.hora_inicial) {
                grupo.novoInicial = eventoPapeleta.hora_inicial;
              }
              if (!grupo.novoFinal && eventoPapeleta.hora_final) {
                grupo.novoFinal = eventoPapeleta.hora_final;
              }
            }
          }
        }
      }
    });

    return Array.from(grupos.values()).sort((a, b) => {
      // Primeiro ordena por data_original (menor para maior)
      const dataA = new Date(a.data_original);
      const dataB = new Date(b.data_original);

      if (dataA.getTime() !== dataB.getTime()) {
        return dataA - dataB;
      }

      // Se as datas são iguais, ordena por hora_original (menor para maior)
      const converterHoraParaMinutos = (hora) => {
        const [horas, minutos] = (hora || "00:00").split(":").map(Number);
        return horas * 60 + minutos;
      };
      const horaA = Math.min(
        ...a.logs.map((log) => converterHoraParaMinutos(log.hora_original))
      );
      const horaB = Math.min(
        ...b.logs.map((log) => converterHoraParaMinutos(log.hora_original))
      );
      return horaA - horaB;
    });
  };

  // Função para obter badge color
  const getBadgeColor = (tipo) => {
    switch (tipo) {
      case "Inserido":
        return "green";
      case "Excluído":
        return "red";
      case "Alterado":
        return "yellow";
      default:
        return "gray";
    }
  };

  // Processamento dos logs usando useMemo para performance
  const logsProcessados = useMemo(() => {
    // Função para encontrar evento correspondente na papeletaData usando id_marcacao
    const encontrarEventoCorrespondente = (log) => {
      if (!papeletaData || !log.id_marcacao) return null;

      // Primeiro tenta encontrar pelo id_marcacao (mais preciso)
      const eventoPorId = papeletaData.find(
        (evento) => evento.id === log.id_marcacao
      );
      if (eventoPorId) return eventoPorId;

      // Se não encontrar, tenta por id_evento + data (fallback)
      return papeletaData.find(
        (evento) =>
          evento.id_evento === log.id_evento &&
          evento.data_inicial === log.data_original
      );
    };

    // Função para obter o nome correto do evento
    const obterNomeEventoCorreto = (log) => {
      // Se temos o evento correspondente na papeleta, usar o nome dele
      const eventoCorrespondente = encontrarEventoCorrespondente(log);
      if (eventoCorrespondente) {
        return (
          eventoCorrespondente.descricao_evento ||
          eventoCorrespondente.nome_evento
        );
      }

      // Caso contrário, usar o nome do log
      return log.nome_evento;
    };

    // Função para verificar se um log corresponde ao estado final na papeleta
    const logCorrespondeAoEstadoFinal = (log) => {
      if (!papeletaData || !log.id_marcacao) return false;

      // Encontrar o evento correspondente na papeleta
      const eventoPapeleta = papeletaData.find(
        (evento) => evento.id === log.id_marcacao
      );
      if (!eventoPapeleta) return false;

      // Verificar se os horários do log correspondem aos horários finais da papeleta
      const campo = log.campo_alteracao;

      if (campo === "horario_inicial" || campo === "inserido_inicial") {
        return log.hora_novo === eventoPapeleta.hora_inicial;
      } else if (campo === "horario_final" || campo === "inserido_final") {
        return log.hora_novo === eventoPapeleta.hora_final;
      }

      return false;
    };

    if (!Array.isArray(logsData)) return [];

    // Todos os logs ordenados
    const logsTodos = logsData.sort((a, b) => {
      const dataA = new Date(a.data_original);
      const dataB = new Date(b.data_original);

      if (dataA.getTime() !== dataB.getTime()) {
        return dataA - dataB;
      }

      const horaA = a.hora_original || "00:00";
      const horaB = b.hora_original || "00:00";
      return horaA.localeCompare(horaB);
    });

    console.log("🔍 Logs totais:", logsTodos.length);

    // Filtrar logs baseado na visão selecionada
    let logsParaExibir;
    if (mostrarTodosLogs) {
      // "Ver todos os logs": incluir todos os logs da API INT_PAPELETA_LOGALTERACOES
      logsParaExibir = logsTodos.filter((log) => {
        // Excluir apenas interstícios
        const nomeEventoCorreto = obterNomeEventoCorreto(log);
        const isIntersticio =
          log.id_evento === "000005" ||
          log.nome_evento?.toLowerCase() === "intersticio" ||
          log.nome_evento?.toLowerCase() === "intersticío" ||
          log.nome_evento?.toLowerCase() === "interstício" ||
          nomeEventoCorreto?.toLowerCase() === "intersticio" ||
          nomeEventoCorreto?.toLowerCase() === "intersticío" ||
          nomeEventoCorreto?.toLowerCase() === "interstício";

        return !isIntersticio;
      });
    } else {
      // "Ver apenas alterações finais": mostrar apenas logs que correspondem ao estado final da papeleta
      logsParaExibir = logsTodos.filter((log) => {
        // Excluir interstícios
        const nomeEventoCorreto = obterNomeEventoCorreto(log);
        const isIntersticio =
          log.id_evento === "000005" ||
          log.nome_evento?.toLowerCase() === "intersticio" ||
          log.nome_evento?.toLowerCase() === "intersticío" ||
          log.nome_evento?.toLowerCase() === "interstício" ||
          nomeEventoCorreto?.toLowerCase() === "intersticio" ||
          nomeEventoCorreto?.toLowerCase() === "intersticío" ||
          nomeEventoCorreto?.toLowerCase() === "interstício";

        if (isIntersticio) return false;

        // Verificar se o log corresponde ao estado final na papeleta
        return logCorrespondeAoEstadoFinal(log);
      });
    }

    console.log("🔍 Logs para exibir após filtros:", logsParaExibir.length);

    const logsAgrupados = agruparLogsPorMarcacao(logsParaExibir);
    console.log("🔍 Logs agrupados:", logsAgrupados.length);

    // Para "Ver apenas alterações finais", consolidar por id_marcacao
    let logsFinais;
    if (mostrarTodosLogs) {
      // "Ver todos": mostrar todos os grupos
      logsFinais = logsAgrupados;
    } else {
      // "Ver apenas alterações finais": consolidar por id_marcacao
      const eventosConsolidados = new Map();

      logsAgrupados.forEach((grupo) => {
        const chave = grupo.id_marcacao;

        if (!eventosConsolidados.has(chave)) {
          eventosConsolidados.set(chave, grupo);
        } else {
          // Se já existe um evento com este id_marcacao, verificar qual é mais recente
          const eventoExistente = eventosConsolidados.get(chave);

          // Comparar pela data/hora da última alteração
          const ultimaAlteracaoExistente = Math.max(
            ...eventoExistente.logs.map((log) =>
              new Date(`${log.data_alteracao}T${log.hora_alteracao}`).getTime()
            )
          );

          const ultimaAlteracaoNovo = Math.max(
            ...grupo.logs.map((log) =>
              new Date(`${log.data_alteracao}T${log.hora_alteracao}`).getTime()
            )
          );

          // Manter o mais recente
          if (ultimaAlteracaoNovo > ultimaAlteracaoExistente) {
            eventosConsolidados.set(chave, grupo);
          }
        }
      });

      logsFinais = Array.from(eventosConsolidados.values());
    }

    console.log("🔍 Logs finais para exibição:", logsFinais.length);

    return logsFinais;
  }, [logsData, mostrarTodosLogs, papeletaData]);

  if (loadingLogs) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="120px"
        flexDirection="column"
        gap={2}
      >
        <Spinner size="lg" color="red.500" thickness="4px" speed="0.7s" />
        <Text color="gray.500" fontWeight="medium">
          Carregando histórico...
        </Text>
      </Box>
    );
  }

  if (logsData.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="80px"
        color="gray.400"
        fontWeight="medium"
      >
        Nenhuma alteração registrada para este período.
      </Box>
    );
  }

  // Debug: verificar se logsProcessados tem dados
  console.log(
    "TabelaHistoricoAlteracoes - logsProcessados length:",
    logsProcessados?.length
  );
  console.log("TabelaHistoricoAlteracoes - logsData length:", logsData?.length);

  return (
    <Box
      id="logs-alteracoes"
      bg="white"
      p={4}
      borderRadius="lg"
      boxShadow="md"
      mt={6}
      borderLeft="6px solid #E31E2C"
      minH="120px"
    >
      <HStack justifyContent="space-between" mb={3}>
        <Text
          fontSize="xl"
          fontWeight="bold"
          color="gray.700"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <MdHistory color="#E31E2C" /> Histórico de Alterações
        </Text>
        <Button
          size="sm"
          colorScheme="red"
          variant="outline"
          onClick={() => setMostrarTodosLogs((prev) => !prev)}
        >
          {mostrarTodosLogs
            ? "Mostrar apenas alterações finais"
            : "Mostrar todos os logs"}
        </Button>
      </HStack>

      <Box overflowX="auto">
        <Table
          variant="simple"
          size="sm"
          borderRadius="lg"
          overflow="hidden"
          minW="900px"
        >
          <Thead bg="red.600">
            <Tr>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Data
              </Th>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Evento
              </Th>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Tipo de Correção
              </Th>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Horário original inicial
              </Th>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Novo horário inicial
              </Th>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Horário original final
              </Th>
              <Th color="white" fontWeight="bold" fontSize="sm">
                Novo horário final
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {logsProcessados.map((grupo, index) => {
              const badgeColor = getBadgeColor(grupo.tipo);
              // Função para obter o nome correto do evento (versão simplificada para renderização)
              const obterNomeEventoCorreto = (log) => {
                if (!papeletaData || !log.id_marcacao) return log.nome_evento;
                const eventoCorrespondente = papeletaData.find(
                  (evento) => evento.id === log.id_marcacao
                );
                return eventoCorrespondente
                  ? eventoCorrespondente.descricao_evento ||
                      eventoCorrespondente.nome_evento
                  : log.nome_evento;
              };
              const nomeEventoCorreto = obterNomeEventoCorreto(grupo);
              const eventoNome = nomeEventoCorreto?.toUpperCase() || "—";
              const emoji = iconesEvento[eventoNome] || "❓";

              return (
                <Tr
                  key={index}
                  bg={index % 2 === 0 ? "gray.50" : "white"}
                  _hover={{ bg: "gray.100" }}
                  transition="background 0.2s"
                >
                  <Td>{formatDate(grupo.data_original) || "—"}</Td>
                  <Td display="flex" alignItems="center" gap={2}>
                    <span>{emoji}</span> {nomeEventoCorreto || "—"}
                  </Td>
                  <Td>
                    <Badge colorScheme={badgeColor}>{grupo.tipo}</Badge>
                  </Td>
                  <Td>{grupo.originalInicial}</Td>
                  <Td>{grupo.novoInicial}</Td>
                  <Td>{grupo.originalFinal}</Td>
                  <Td>{grupo.novoFinal}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default TabelaHistoricoAlteracoes;
